import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import React__default, { forwardRef, useId, useState, useEffect, useCallback, useMemo } from 'react';
import { ExclamationCircleIcon } from '@iris/react-icons/ExclamationCircleIcon';
import { MenuDownIcon } from '@iris/react-icons/MenuDownIcon';
import { MenuUpIcon } from '@iris/react-icons/MenuUpIcon';
import * as RadixSelect from '@radix-ui/react-select';
import { INPUT_VARIANT } from '../../utils/constant.esm.js';
import baseInputStyles from '../BaseInput/BaseInput.styles.esm.js';
import { BaseLabel } from '../BaseLabel/BaseLabel.esm.js';
import { InlineMessage } from '../InlineMessage/InlineMessage.esm.js';
import { Popover } from '../Popover/Popover.esm.js';
import popoverStyles from '../Popover/Popover.styles.esm.js';
import { Sheet } from '../Sheet/Sheet.esm.js';
import inputStyles from '../TextInput/TextInput.styles.esm.js';
import componentStyles from './SelectDropdown.styles.esm.js';
import { SelectDropdownItem } from './SelectDropdownItem.esm.js';
import SelectDropdownTriggerStyles from './SelectDropdownTrigger.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const SelectDropdown = forwardRef((_a, ref) => {
    var { children, inverse = false, className, popoverClassName, disabled = false, onValueChange, optionGroups = [], value, label, variant = INPUT_VARIANT.Outer, placeholder, error = false, errorMessage, "aria-label": ariaLabel = "", showQuantity = false } = _a, props = __rest(_a, ["children", "inverse", "className", "popoverClassName", "disabled", "onValueChange", "optionGroups", "value", "label", "variant", "placeholder", "error", "errorMessage", "aria-label", "showQuantity"]);
    const { container: containerClasses, group: groupClasses, sheet: sheetClasses, sheetBody: sheetBodyClasses, popover: internalPopoverClasses, labelInner: labelInnerClasses, } = componentStyles({
        inverse,
        disabled,
    });
    const popoverId = useId();
    const generatedId = useId();
    const selectDropdownClasses = SelectDropdownTriggerStyles({
        inverse,
        disabled,
        error,
        variant,
    });
    const baseInputClasses = baseInputStyles({
        inverse,
    });
    const textInputStyles = inputStyles({
        inverse,
    });
    const popoverClasses = popoverStyles({ inverse, class: className });
    const [isOpen, setIsOpen] = React__default.useState(false);
    const [selectedValue, setSelectedValue] = useState(value);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const [size, setSize] = useState(null);
    // Use a state to track if navigation is being used
    const [useNavigation, setUseNavigation] = useState(null);
    const [searchChar, setSearchChar] = useState(null);
    const [lastSearchIndex, setLastSearchIndex] = useState(-1);
    const [triggerWidth, setTriggerWidth] = useState(undefined);
    const triggerRef = React__default.useRef(null);
    const popoverRef = React__default.useRef(null);
    const sheetRef = React__default.useRef(null);
    const pendingFocusIndexRef = React__default.useRef(null);
    // Sync selectedValue with value prop changes
    useEffect(() => {
        // Check if the value exists in the optionGroups
        const foundItem = optionGroups.some((group) => {
            return group.children.some((item) => {
                if (item.value === value) {
                    setSelectedValue(item.value);
                    return true;
                }
                return false;
            });
        });
        // If no matching item is found, reset selectedValue
        if (!foundItem) {
            setSelectedValue(undefined);
        }
    }, [optionGroups, value]);
    // Helper function to extract width values and detect width classes
    const extractWidthConfig = useCallback((classNameStr) => {
        if (!classNameStr) {
            return {
                extractedWidth: undefined,
                hasStandardWidthClass: false,
            };
        }
        // Extract arbitrary width values like w-[300px]
        const arbitraryWidthMatch = classNameStr.match(/iris:w-\[([^\]]+)\]/);
        const extractedWidth = arbitraryWidthMatch === null || arbitraryWidthMatch === void 0 ? void 0 : arbitraryWidthMatch[1];
        // Check if className contains standard width classes
        const hasStandardWidthClass = classNameStr.includes("w-") ||
            classNameStr.includes("min-w-") ||
            classNameStr.includes("max-w-");
        return {
            extractedWidth,
            hasStandardWidthClass,
        };
    }, []);
    // Memoize both container and popover classNames with dynamic width extraction
    const { memoizedPopoverClassName, memoizedContainerClassName, customWidth, shouldUseInlineWidth, containerCustomWidth, shouldUseContainerInlineWidth, } = useMemo(() => {
        const popoverConfig = extractWidthConfig(popoverClassName);
        const containerConfig = extractWidthConfig(className);
        return {
            memoizedPopoverClassName: cn(popoverClasses.content(), internalPopoverClasses(), popoverClassName),
            memoizedContainerClassName: cn(containerClasses(), className || "iris:w-[300px]"),
            customWidth: popoverConfig.extractedWidth,
            shouldUseInlineWidth: !popoverConfig.hasStandardWidthClass ||
                !!popoverConfig.extractedWidth,
            containerCustomWidth: containerConfig.extractedWidth,
            shouldUseContainerInlineWidth: !containerConfig.hasStandardWidthClass ||
                !!containerConfig.extractedWidth,
        };
    }, [
        popoverClasses,
        internalPopoverClasses,
        popoverClassName,
        containerClasses,
        className,
        extractWidthConfig,
    ]);
    const handleValueChange = (value) => {
        setSelectedValue(value);
        if (onValueChange) {
            onValueChange(value);
        }
    };
    const getCurrentRef = React__default.useCallback(() => {
        return size === "desktop" ? popoverRef : sheetRef;
    }, [size]);
    const getEnabledElement = useCallback((query) => {
        const currentRef = getCurrentRef();
        if (!currentRef.current)
            return [];
        return Array.from(currentRef.current.querySelectorAll(query ? query : `li[role="option"]:not([aria-disabled="true"])`));
    }, [getCurrentRef]);
    const focusButtonAt = useCallback((index) => {
        const buttons = getEnabledElement();
        if (buttons.length === 0)
            return;
        const clamped = Math.max(0, Math.min(index, buttons.length - 1));
        buttons[clamped].focus();
    }, [getEnabledElement]);
    const getFocusedButtonIndex = () => {
        const buttons = getEnabledElement();
        return buttons.findIndex((btn) => btn === document.activeElement);
    };
    // Function to render the list of options
    const onKeydown = (e) => {
        var _a, _b, _c;
        const buttons = getEnabledElement();
        const currentIndex = getFocusedButtonIndex();
        switch (e.key) {
            case "Escape": {
                e.preventDefault();
                setIsOpen(false); // Close the ListBox
                setUseNavigation(null); // Reset navigation state
                (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); // Return focus to the trigger
                break;
            }
            case "ArrowDown": {
                e.preventDefault();
                if (buttons.length === 0)
                    return;
                const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % buttons.length;
                focusButtonAt(nextIndex);
                setFocusedIndex(nextIndex);
                break;
            }
            case "ArrowUp": {
                e.preventDefault();
                if (buttons.length === 0)
                    return;
                const prevIndex = currentIndex < 0
                    ? buttons.length - 1
                    : (currentIndex - 1 + buttons.length) % buttons.length;
                focusButtonAt(prevIndex);
                setFocusedIndex(prevIndex);
                break;
            }
            case "Home":
                e.preventDefault();
                setFocusedIndex(0);
                focusButtonAt(0);
                break;
            case "End":
                setFocusedIndex(buttons.length - 1);
                e.preventDefault();
                focusButtonAt(buttons.length - 1);
                break;
            case "Tab": {
                const buttons = getEnabledElement('button[aria-label="close"]');
                if (buttons && buttons.length > 0) {
                    e.preventDefault();
                    const closeButton = buttons[0];
                    if (closeButton && document.activeElement === buttons[0]) {
                        focusButtonAt(focusedIndex !== null && focusedIndex !== void 0 ? focusedIndex : 0);
                        setFocusedIndex(focusedIndex !== null && focusedIndex !== void 0 ? focusedIndex : 0);
                    }
                    else {
                        closeButton === null || closeButton === void 0 ? void 0 : closeButton.focus();
                    }
                    return;
                }
                if (e.shiftKey) {
                    // Handle Shift+Tab back go to the trigger
                    (_b = triggerRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    e.preventDefault();
                }
                break;
            }
            default:
                if (e.key.length === 1 && /\S/.test(e.key)) {
                    const char = e.key.toLowerCase();
                    let startIdx = 0;
                    if (searchChar === char && lastSearchIndex !== -1) {
                        // Continue searching from the next button
                        startIdx = (lastSearchIndex + 1) % buttons.length;
                    }
                    let foundIdx = -1;
                    for (let i = 0; i < buttons.length; i++) {
                        const idx = (startIdx + i) % buttons.length;
                        const text = ((_c = buttons[idx].textContent) === null || _c === void 0 ? void 0 : _c.trim().toLowerCase()) || "";
                        if (text.startsWith(char)) {
                            foundIdx = idx;
                            break;
                        }
                    }
                    if (foundIdx !== -1) {
                        focusButtonAt(foundIdx);
                        setFocusedIndex(foundIdx);
                        setSearchChar(char);
                        setLastSearchIndex(foundIdx);
                    }
                    else {
                        setSearchChar(null);
                        setLastSearchIndex(-1);
                    }
                }
                break;
        }
    };
    const List = (ref) => {
        return (jsxs("div", { role: "listbox", "aria-activedescendant": selectedValue, ref: ref, tabIndex: -1, className: "iris:w-full", children: [children, optionGroups.map((group, groupIdx) => (jsxs("ul", { role: "group", "aria-labelledby": `header-group-${groupIdx}`, children: [jsx("li", { id: `header-group-${groupIdx}`, className: groupClasses(), role: "presentation", "aria-hidden": "true", children: group.label }), group.children.map((item) => {
                            const isSelected = item.value === selectedValue;
                            return (jsx(SelectDropdownItem, { item: item, tabIndex: isSelected ? 0 : -1, selectedValue: selectedValue, showQuantity: showQuantity, inverse: inverse, onClick: (value) => {
                                    var _a;
                                    setSelectedValue(value);
                                    if (onValueChange) {
                                        onValueChange(value);
                                        (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                    }
                                    setIsOpen(false);
                                } }, item.value));
                        })] }, groupIdx)))] }));
    };
    const renderRadixValue = () => {
        var _a, _b, _c, _d;
        if (variant === INPUT_VARIANT.Outer) {
            return selectedValue ? (jsx("span", { className: cn(textInputStyles.label(), textInputStyles.labelOuter), "aria-hidden": "true", children: (_b = (_a = optionGroups
                    .flatMap((_optionGroups) => _optionGroups.children)) === null || _a === void 0 ? void 0 : _a.find((_option) => _option.value === selectedValue)) === null || _b === void 0 ? void 0 : _b.label })) : (jsxs(Fragment, { children: [jsx(BaseLabel, { className: "iris:sr-only", children: label }), jsx(RadixSelect.Value, { placeholder: placeholder })] }));
        }
        else {
            return (jsxs("div", { className: cn(textInputStyles.labelInputWrapper()), children: [jsx(BaseLabel, { variant: "inner", id: `${generatedId}-label`, inverse: inverse, disabled: disabled, "data-hasvalue": selectedValue ? "true" : "false", className: cn(selectDropdownClasses.labelBase(), selectDropdownClasses.labelInner()), children: label }), jsx("span", { className: cn(textInputStyles.input(), labelInnerClasses()), "aria-hidden": "true", "data-hasvalue": selectedValue ? "true" : "false", children: (_d = (_c = optionGroups
                            .flatMap((_optionGroups) => _optionGroups.children)) === null || _c === void 0 ? void 0 : _c.find((_option) => _option.value === selectedValue)) === null || _d === void 0 ? void 0 : _d.label })] }));
        }
    };
    const onDesktopPointerDownOutside = (event) => {
        event.preventDefault();
        if (triggerRef.current === event.target) {
            return;
        }
        if (size === "desktop") {
            setIsOpen(false);
            if (focusedIndex !== null && useNavigation === true) {
                setTimeout(() => {
                    var _a;
                    (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, 0);
            }
        }
    };
    React__default.useEffect(() => {
        if (isOpen && size && useNavigation === true) {
            // If last focused index is not null, focus into it
            if (focusedIndex !== null) {
                focusButtonAt(focusedIndex);
                setFocusedIndex(focusedIndex);
                return;
            }
            // Get Selected element and focus into it
            let focusIdx = 0;
            if (selectedValue) {
                const focusableButtons = getEnabledElement();
                const selectedIdx = focusableButtons.findIndex((el) => el.getAttribute("data-value") === selectedValue);
                if (selectedIdx !== -1) {
                    focusIdx = selectedIdx;
                }
            }
            focusButtonAt(focusIdx);
            setFocusedIndex(focusIdx);
        }
    }, [
        focusButtonAt,
        focusedIndex,
        getEnabledElement,
        isOpen,
        selectedValue,
        size,
        useNavigation,
    ]);
    React__default.useEffect(() => {
        if (isOpen && pendingFocusIndexRef.current !== null) {
            focusButtonAt(pendingFocusIndexRef.current);
            setFocusedIndex(pendingFocusIndexRef.current);
            pendingFocusIndexRef.current = null;
        }
    }, [isOpen, focusButtonAt]);
    React__default.useEffect(() => {
        if (!isOpen) {
            // Reset useNavigation when dropdown is closed
            setUseNavigation(null);
            setFocusedIndex(null);
        }
        else if (triggerRef.current) {
            // Update trigger width when popover opens
            const width = triggerRef.current.offsetWidth;
            setTriggerWidth(width);
        }
    }, [isOpen]);
    return (jsxs("div", Object.assign({ ref: ref, className: memoizedContainerClassName, style: Object.assign({}, (shouldUseContainerInlineWidth
            ? {
                width: containerCustomWidth ? containerCustomWidth : undefined,
            }
            : {})) }, props, { children: [jsxs("div", { className: selectDropdownClasses.container(), children: [error && errorMessage && (jsx(InlineMessage, { variant: "error", label: errorMessage, icon: jsx(ExclamationCircleIcon, {}), iconClass: selectDropdownClasses.inlineMsg(), labelClass: selectDropdownClasses.inlineMsg() })), jsx(RadixSelect.Root, { value: selectedValue, onValueChange: handleValueChange, open: isOpen, disabled: disabled, children: jsxs(RadixSelect.Trigger, Object.assign({ tabIndex: 0, ref: triggerRef, "data-state": error ? "error" : "default", className: cn("iris:flex iris:justify-between", baseInputClasses, selectDropdownClasses.trigger()), onClick: (e) => {
                                setIsOpen(!isOpen);
                                e.preventDefault();
                                setUseNavigation(false);
                            }, onKeyDown: (e) => {
                                var _a;
                                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const itemsCount = ((_a = optionGroups.flatMap((_g) => _g.children)) === null || _a === void 0 ? void 0 : _a.length) || 0;
                                    if (!isOpen) {
                                        // Defer focus until after the options are rendered
                                        pendingFocusIndexRef.current =
                                            e.key === "ArrowDown" ? 0 : Math.max(itemsCount - 1, 0);
                                        setIsOpen(true);
                                    }
                                    else {
                                        if (e.key === "ArrowDown") {
                                            focusButtonAt(0);
                                            setFocusedIndex(0);
                                        }
                                        else {
                                            const lastIdx = Math.max(itemsCount - 1, 0);
                                            focusButtonAt(lastIdx);
                                            setFocusedIndex(lastIdx);
                                        }
                                    }
                                    if (useNavigation === null) {
                                        setUseNavigation(true);
                                    }
                                    return;
                                }
                                if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsOpen(!isOpen);
                                }
                                if (useNavigation === null) {
                                    setUseNavigation(true);
                                }
                            } }, (ariaLabel ? { "aria-label": ariaLabel } : {}), { children: [renderRadixValue(), jsx(RadixSelect.Icon, { className: selectDropdownClasses.icon(), children: !(isOpen !== undefined ? isOpen : isOpen) ? (jsx(MenuDownIcon, {})) : (jsx(MenuUpIcon, {})) })] })) }), variant === INPUT_VARIANT.Outer && (jsx(BaseLabel, { inverse: inverse, disabled: disabled, className: cn(selectDropdownClasses.labelBase()), "aria-hidden": "true", onClick: (e) => {
                            e.preventDefault();
                            if (!disabled && !isOpen)
                                setIsOpen(true);
                        }, children: label }))] }), jsx(Sheet, { title: label, hasHeader: true, open: isOpen, onOpenChange: (value) => {
                    if (!value && size !== "mobile")
                        return;
                    setIsOpen(value);
                }, ref: sheetRef, onKeyDown: onKeydown, className: sheetClasses(), bodyClassName: sheetBodyClasses(), onOpenAutoFocus: (e) => {
                    e.preventDefault();
                }, onAnimationStart: (e) => {
                    if (e.animationName === "bottomSheetIn") {
                        setSize("mobile");
                    }
                    else if (e.animationName === "bottomSheetOut") {
                        setSize(null);
                    }
                }, children: List() }), jsx(Popover, { isOpen: isOpen, onOpenChange: (value) => {
                    if (!value && size !== "desktop")
                        return;
                    setIsOpen(value);
                }, style: Object.assign({ height: "fit-content" }, (shouldUseInlineWidth
                    ? {
                        width: customWidth
                            ? customWidth
                            : triggerWidth
                                ? `${triggerWidth}px`
                                : undefined,
                    }
                    : {})), hasAsChild: false, id: popoverId, className: memoizedPopoverClassName, sideOffset: 8, onKeyDown: onKeydown, onFocusOutside: (e) => {
                    e.preventDefault();
                }, onPointerDownOutside: onDesktopPointerDownOutside, role: "listbox", onAnimationStart: (e) => {
                    if (e.animationName === "popoverBottomIn") {
                        setSize("desktop");
                    }
                    else if (e.animationName === "popoverBottomOut") {
                        setSize(null);
                    }
                }, onOpenAutoFocus: (event) => {
                    event.preventDefault();
                }, onCloseAutoFocus: (event) => {
                    event.preventDefault();
                }, tabIndex: -1, children: List(popoverRef) })] })));
});
SelectDropdown.displayName = "SelectDropdown";

export { SelectDropdown };
